import React, { useEffect, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { Tablero } from "../../../services/v2/prospects.service";
import { cuando, dinero, errorTexto, SIMBOLO_PLANTEL } from "./salesHelpers";

interface Props { campusId: number; onSalir: () => void; }

const mesesAtras = (n: number): { v: string; t: string }[] => {
	const out = [];
	const d = new Date();
	for (let i = 0; i < n; i++) {
		const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
		const v = m.getFullYear() + "-" + String(m.getMonth() + 1).padStart(2, "0");
		out.push({ v, t: m.toLocaleDateString("es-MX", { month: "long", year: "numeric" }) });
	}
	return out;
};

const num = (v: number) => Number(v).toLocaleString("es-MX", { maximumFractionDigits: 1 });

/**
 * Tablero de Ventas (lienzo): matrículas del mes por equipo, asesora y semana, creaciones de hoy y anuncios.
 * Reemplaza el informe anual de ventas hecho a mano. Cuenta lo que entró por solicitud de matrícula aprobada.
 */
const TableroView: React.FC<Props> = ({ campusId, onSalir }) => {
	const http = HttpManager.getInstance();
	const [mes, setMes] = useState<string>(mesesAtras(1)[0].v);
	const [scope, setScope] = useState<"plantel" | "pais">("plantel");
	const [t, setT] = useState<Tablero | null>(null);
	const [error, setError] = useState<string | null>(null);
	const sim = SIMBOLO_PLANTEL[campusId] || "";

	useEffect(() => {
		setT(null); setError(null);
		http.prospectsService.dashboard(campusId, mes, scope === "pais" ? "pais" : null).then((r) => setT(r.data)).catch((e) => setError(errorTexto(e)));
	}, [campusId, mes, scope]);

	const excel = () => {
		if (!t) return;
		const filas = [["Equipo", "Matrículas", "Monto", "Avance %", "Falta"], ...t.equipos.map((e) => [e.equipo, e.mat, e.monto, e.avance ?? "", e.falta]),
			[], ["Asesora", "Equipo", "Matrículas", "Monto"], ...t.asesoras.map((a) => [a.asesora, a.equipo, a.mat, a.monto]),
			[], ["Semana", "Matrículas", "Monto"], ...t.semanas.map((s) => [s.semana, s.mat, s.monto]),
			[], ["Anuncio", "Red", "Leads", "Matrículas", "Conv %"], ...t.anuncios.map((a) => [a.anuncio, a.red, a.leads, a.mat, a.conv])];
		const csv = "﻿" + filas.map((f) => f.map((c) => '"' + String(c ?? "").replace(/"/g, '""') + '"').join(",")).join("\n");
		const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
		const a = document.createElement("a"); a.href = url; a.download = `ventas_${t.mes}.csv`; a.click();
		setTimeout(() => URL.revokeObjectURL(url), 2000);
	};

	return (
		<div className="tb">
			<div className="tb-cab">
				<div>
					<h2>Tablero de Ventas</h2>
					<span className="apagado">{t ? t.mesTexto : ""}{scope === "pais" ? " · todo el país" : ""}</span>
				</div>
				<div className="tb-herr">
					<select value={scope} onChange={(e) => setScope(e.target.value as any)} aria-label="Alcance">
						<option value="plantel">Este plantel</option>
						<option value="pais">Todo el país</option>
					</select>
					<select value={mes} onChange={(e) => setMes(e.target.value)} aria-label="Mes">
						{mesesAtras(12).map((m) => <option key={m.v} value={m.v}>{m.t}</option>)}
					</select>
					<button className="btn-wa" onClick={excel} disabled={!t}>Excel</button>
					<button className="enlace" onClick={onSalir}>Volver a la bandeja</button>
				</div>
			</div>
			{error ? <div className="aviso error">{error}</div> : null}
			{!t ? <div className="ventas-vacio">Cargando…</div> : (
				<div className="tb-cuerpo">
					<div className="tb-kpis">
						<div className="tb-kpi"><span>Matrículas</span><b>{num(t.matriculas)}</b></div>
						<div className="tb-kpi"><span>Monto cobrado</span><b>{dinero(t.monto, sim)}</b></div>
						<div className="tb-kpi"><span>Creaciones hoy</span><b>{t.creacionesHoy}</b></div>
						<div className="tb-kpi"><span>Falta para meta</span><b>{num(t.faltaMeta)}</b></div>
						<div className="tb-kpi"><span>Sin asesora</span><b className={t.sinAsesora ? "oro" : ""}>{num(t.sinAsesora)}</b></div>
					</div>

					<div className="tb-grid">
						<div className="tarjeta">
							<div className="tarjeta-cab"><h4>Equipos</h4><span className="apagado">meta {t.meta}</span></div>
							<table className="tb-tabla">
								<thead><tr><th>Equipo</th><th>Mat</th><th>Monto</th><th>Avance</th><th>Falta</th></tr></thead>
								<tbody>
									{t.equipos.map((e) => (
										<tr key={e.equipo}><td>{e.equipo}</td><td>{num(e.mat)}</td><td>{dinero(e.monto, sim)}</td>
											<td><div className="tb-barra"><span style={{ width: Math.min(100, e.avance || 0) + "%" }} /></div></td><td>{num(e.falta)}</td></tr>
									))}
									<tr className="total"><td>Total</td><td>{num(t.matriculas)}</td><td>{dinero(t.monto, sim)}</td><td /><td>{num(t.faltaMeta)}</td></tr>
								</tbody>
							</table>
						</div>
						<div className="tarjeta">
							<h4>Por semana</h4>
							<table className="tb-tabla">
								<thead><tr><th>Días</th><th>Mat</th><th>Monto</th></tr></thead>
								<tbody>{t.semanas.map((s) => <tr key={s.semana}><td>{s.semana}</td><td>{num(s.mat)}</td><td>{dinero(s.monto, sim)}</td></tr>)}</tbody>
							</table>
						</div>
						<div className="tarjeta">
							<div className="tarjeta-cab"><h4>Creaciones de hoy</h4><span className="apagado">{t.creacionesHoy}{t.creaciones[0]?.ultima ? " · última " + cuando(t.creaciones[0].ultima) : ""}</span></div>
							{t.creaciones.map((c) => <div key={c.asesora} className="par"><span>{c.asesora}</span><span>{c.n}</span></div>)}
							{!t.creaciones.length ? <span className="apagado">Nadie nuevo todavía hoy.</span> : null}
						</div>
						<div className="tarjeta">
							<h4>Asesoras</h4>
							<table className="tb-tabla">
								<thead><tr><th>Asesora</th><th>Equipo</th><th>Mat</th><th>Monto</th></tr></thead>
								<tbody>{t.asesoras.map((a) => <tr key={a.asesora}><td>{a.asesora}</td><td className="apagado">{a.equipo}</td><td>{num(a.mat)}</td><td>{dinero(a.monto, sim)}</td></tr>)}</tbody>
							</table>
						</div>
						<div className="tarjeta">
							<h4>Anuncios</h4>
							<table className="tb-tabla">
								<thead><tr><th>Red · anuncio</th><th>Leads</th><th>Mat</th><th>Conv.</th></tr></thead>
								<tbody>{t.anuncios.map((a) => <tr key={a.anuncio + a.red}><td>{a.anuncio}<span className="apagado"> · {a.red}</span></td><td>{a.leads}</td><td>{a.mat}</td><td>{a.conv}%</td></tr>)}</tbody>
							</table>
							{!t.anuncios.length ? <span className="apagado">Sin leads con anuncio este mes.</span> : null}
						</div>
						<div className="tarjeta">
							<div className="tarjeta-cab"><h4>Trimestre</h4><span className="apagado">desde {t.trimestre.desde} · {num(t.trimestre.matriculas)} matrículas · {dinero(t.trimestre.monto, sim)}</span></div>
							{t.trimestre.equipos.map((e) => <div key={e.equipo} className="par"><span>{e.equipo}</span><span>{num(e.mat)} <span className="apagado">/ {t.trimestre.metaTrimestre}</span></span></div>)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default TableroView;
